<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import { SidebarLayout } from '@hantera/design-system'
import { useRegistry, useContext, useLocalization } from '@hantera/portal-app'
import InventoryList from './InventoryList.vue'
import InventoryEditor from './InventoryEditor.vue'

const context = useContext()
const t = useLocalization('settings.inventories')
const registry = useRegistry()

watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = t('title')
  }
})

const canRead = computed(() => context.authorize('registry/inventories:read'))

interface InventoryItem {
  key: string
  data: Record<string, any>
}

const inventories = ref<InventoryItem[]>([])
const selectedKey = ref<string | null>(null)
const loading = ref(true)

const selectedInventory = computed(() => {
  if (!selectedKey.value) return null
  return inventories.value.find(i => i.key === selectedKey.value) ?? null
})

async function loadInventories() {
  loading.value = true
  try {
    const data = await registry.list('inventories', true)
    inventories.value = Object.entries(data).map(([fullKey, value]) => ({
      key: fullKey.split('/').pop()!,
      data: typeof value === 'string' ? JSON.parse(value) : value
    })).sort((a, b) => a.key.localeCompare(b.key))
  } catch (e) {
    console.error('Failed to load inventories:', e)
  } finally {
    loading.value = false
  }
}

function handleSelect(key: string | null) {
  selectedKey.value = key
}

function handleSaved() {
  loadInventories()
}

function handleDeleted() {
  selectedKey.value = null
  loadInventories()
}

function handleCreated(key: string) {
  loadInventories()
  selectedKey.value = key
}

loadInventories()
</script>

<template>
  <SidebarLayout cache-key="inventories-view" v-if="canRead">
    <template #sidebar>
      <InventoryList
        :inventories="inventories"
        :selected-key="selectedKey"
        :loading="loading"
        @select="handleSelect"
        @created="handleCreated"
      />
    </template>
    <template #content>
      <InventoryEditor
        v-if="selectedInventory"
        :inventory-key="selectedInventory.key"
        :data="selectedInventory.data"
        @saved="handleSaved"
        @deleted="handleDeleted"
      />
      <div v-else class="empty-state">
        <p>{{ t('empty') }}</p>
      </div>
    </template>
  </SidebarLayout>
</template>

<style scoped>
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-secondary, hsl(var(--color-40)));
}
</style>
